"use client";

import React from "react";
import Image from "next/image";
import Grid from "@mui/material/Grid";
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Avatar,
  useTheme,
  Paper,
} from "@mui/material";
import { motion } from "framer-motion";

type Partner = {
  id: string;
  title: string;
  logoSrc?: string;
  description: string;
};

const PARTNERS: Partner[] = [
  {
    id: "i1",
    title: "IIT Mandi",
    logoSrc: "/images/iit-mandi.png",
    description:
      "Partnered with IIT Mandi, Shivalik College of Engineering offers a world-class education that aligns with emerging technologies. IIT-integrated specialization programs in Data Science and IoT provide mentorship from IIT experts.",
  },
  {
    id: "i2",
    title: "IIT Ropar",
    logoSrc: "/images/iit-ropar.png",
    description:
      "In partnership with IIT Ropar, the college offers B.Tech specialization in AI & ML. The curriculum covers machine learning, deep learning, NLP, computer vision, and AI ethics.",
  },
  {
    id: "i3",
    title: "IIT Roorkee",
    logoSrc: "/images/iit-roorkee.png",
    description:
      "In collaboration with IIT Roorkee, the college provides industry-aligned specialization in AI & Deep Learning. Focused on experiential learning, research exposure, and innovation.",
  },
  {
    id: "i4",
    title: "Larsen & Toubro Limited",
    logoSrc: "/images/lnt.png",
    description:
      "Through collaboration with L&T EduTech, Shivalik College offers skill-driven programs in engineering and management — focusing on Digital Manufacturing, FinTech, and Business Analytics.",
  },
];

const containerVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { staggerChildren: 0.12 } },
};

const cardVariants = {
  hidden: { opacity: 0, y: 18, scale: 0.98 },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.45 },
  },
};

export default function Collaborations(): React.ReactElement {
  const theme = useTheme();

  return (
    <Box
      component="section"
      sx={{
        position: "relative",
        width: "100%",
        py: { xs: 6, sm: 8, md: 12 },
        overflow: "hidden",
      }}
    >
      {/* Background Image (positioned behind content) */}
      <Box aria-hidden="true" sx={{ position: "absolute", inset: 0, zIndex: 0 }}>
        <Box sx={{ position: "absolute", inset: 0, pointerEvents: "none" }} />
        <Box sx={{ position: "relative", width: "100%", height: "100%" }}>
          <Image src="/images/Hero.jpg" alt="campus background" fill sizes="(max-width: 768px) 100vw, 1600px" style={{ objectFit: "cover", objectPosition: "center" }} priority />
        </Box>
      </Box>

      {/* Foreground Content */}
      <Container
        maxWidth="lg"
        sx={{
          position: "relative",
          zIndex: 2,
          px: { xs: 2, sm: 3, md: 4 },
        }}
      >
        {/* Section Heading */}
        <Box textAlign="center" mb={{ xs: 5, md: 6 }}>
          <Typography
            variant="h4"
            component="h2"
            sx={{
              fontWeight: 800,
              color: "#fff",
              letterSpacing: 0.3,
              textShadow: "0 2px 8px rgba(0,0,0,0.6)",
              fontSize: { xs: "1.6rem", sm: "2rem", md: "2.4rem" },
            }}
          >
            Industry & Institute Collaborations
          </Typography>

          <Typography
            variant="subtitle1"
            sx={{
              mt: 1,
              color: "rgba(255,255,255,0.85)",
              fontSize: { xs: 12.5, sm: 13, md: 14 },
            }}
          >
            Innovating through strategic partnerships
          </Typography>
        </Box>

        {/* Cards Section */}
        <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={containerVariants}>
          <Grid container spacing={{ xs: 2.5, sm: 3, md: 4 }} justifyContent="center" alignItems="stretch">
            {PARTNERS.map((p) => (
              <Grid key={p.id} size={{ xs: 12, sm: 6, md: 6, lg: 6 }}>
                <motion.div variants={cardVariants}>
                  <Card
                    component={Paper}
                    elevation={8}
                    sx={{
                      width: "100%",
                      borderRadius: 3,
                      minHeight: 260,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "flex-start",
                      textAlign: "center",
                      px: { xs: 2.5, sm: 3, md: 4 },
                      py: { xs: 3, sm: 3.5, md: 4 },
                      background: "linear-gradient(180deg, rgba(255,255,255,0.97), rgba(250,250,250,0.93))",
                      backdropFilter: "blur(6px)",
                      border: "1px solid rgba(255,255,255,0.6)",
                      boxShadow: "0 8px 24px rgba(2,6,23,0.55), inset 0 1px 0 rgba(255,255,255,0.6)",
                      transition: "transform .24s ease, box-shadow .24s ease",
                      "&:hover": {
                        transform: "translateY(-6px)",
                        boxShadow: "0 16px 48px rgba(2,6,23,0.55)",
                      },
                    }}
                  >
                    {/* Partner Logo */}
                    <Box
                      sx={{
                        width: { xs: 90, sm: 100, md: 110 },
                        height: { xs: 90, sm: 100, md: 110 },
                        borderRadius: "999px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        overflow: "hidden",
                        border: `3px solid ${theme.palette.primary.main}`,
                        bgcolor: "#fff",
                        mb: 1.5,
                        mt: { xs: 5, md: 0 },
                      }}
                    >
                      {p.logoSrc ? (
                        <Image src={p.logoSrc} alt={`${p.title} logo`} width={90} height={90} style={{ objectFit: "contain", width: "100%", height: "100%", padding: 8 }} />
                      ) : (
                        <Avatar sx={{ width: "100%", height: "100%", bgcolor: "#fff", color: theme.palette.primary.main, fontWeight: 700, fontSize: { xs: 20, md: 24 } }}>
                          {p.title
                            .split(" ")
                            .slice(0, 2)
                            .map((w) => (w ? w[0] : ""))
                            .join("")}
                        </Avatar>
                      )}
                    </Box>

                    {/* Text */}
                    <CardContent sx={{ flex: "1 1 auto", pt: 1 }}>
                      <Typography variant="h6" component="h3" sx={{ fontWeight: 700, mb: 0.8, color: "text.primary", fontSize: { xs: "1rem", sm: "1.05rem", md: "1.1rem" } }}>
                        {p.title}
                      </Typography>

                      <Typography variant="body2" sx={{ color: "text.secondary", mt: 1, lineHeight: 1.5, fontSize: { xs: 12.5, sm: 13, md: 13.5 }, maxWidth: 540, mx: "auto" }}>
                        {p.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>
      </Container>
    </Box>
  );
}